import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FileText,
  CheckCircle,
  Hash,
  Palette,
  HardHat,
  Trash2,
  Calendar,
  Clock,
  Loader2,
  ToggleLeft,
  ToggleRight,
  Save,
} from 'lucide-react';
import { FormModal, MODAL_COLORS, useModal } from '@/components/modal';
import { useUpdateTreatmentType, useDeactivateTreatmentType, useHelmetTypes, useSetTreatmentTypeHelmets } from '@/features/reports/hooks';
import { triggerHapticFeedback } from '../utils/adminAnimations';
import { localizedName } from '@/utils/localized';
import toast from 'react-hot-toast';
import type { TreatmentTypeDefinition } from '@/features/reports/types';

const cardStyle = {
  backgroundColor: MODAL_COLORS.interactive,
  border: `1px solid ${MODAL_COLORS.border}`,
};

const actionBtnStyle = {
  border: `1px solid ${MODAL_COLORS.border}`,
};

const inputStyle = {
  backgroundColor: MODAL_COLORS.surface,
  border: `1px solid ${MODAL_COLORS.border}`,
  color: MODAL_COLORS.text,
};

interface TreatmentTypeDetailModalProps {
  treatmentType: TreatmentTypeDefinition;
  onClose: () => void;
}

export function TreatmentTypeDetailModal({ treatmentType, onClose }: TreatmentTypeDetailModalProps) {
  const { t, i18n } = useTranslation();
  const { openConfirm } = useModal();
  const updateMutation = useUpdateTreatmentType();
  const deleteMutation = useDeactivateTreatmentType();
  const { data: helmetTypesData } = useHelmetTypes({ isActive: 'true', limit: 100 });
  const allHelmetTypes = helmetTypesData?.helmetTypes ?? [];
  const helmetMutation = useSetTreatmentTypeHelmets();
  const [isOpen, setIsOpen] = useState(true);
  const requestClose = useCallback(() => setIsOpen(false), []);

  // Editable fields
  const [name, setName] = useState(treatmentType.name);
  const [nameEn, setNameEn] = useState(treatmentType.nameEn ?? '');
  const [description, setDescription] = useState(treatmentType.description ?? '');
  const [color, setColor] = useState(treatmentType.color ?? '');

  const hasChanges =
    name !== treatmentType.name ||
    nameEn !== (treatmentType.nameEn ?? '') ||
    description !== (treatmentType.description ?? '') ||
    color !== (treatmentType.color ?? '');

  const handleSave = async () => {
    if (!name.trim()) return;
    try {
      triggerHapticFeedback();
      await updateMutation.mutateAsync({
        id: treatmentType.id,
        data: {
          name: name.trim(),
          nameEn: nameEn.trim() || undefined,
          description: description.trim() || undefined,
          color: color.trim() || undefined,
        },
      });
      toast.success(t('treatmentsAdmin.updateSuccess'));
      requestClose();
    } catch {
      toast.error(t('treatmentsAdmin.updateFailed'));
    }
  };

  const handleToggleActive = async () => {
    try {
      triggerHapticFeedback();
      await updateMutation.mutateAsync({
        id: treatmentType.id,
        data: { isActive: !treatmentType.isActive },
      });
      toast.success(
        t(treatmentType.isActive ? 'treatmentsAdmin.deactivateSuccess' : 'treatmentsAdmin.activateSuccess')
      );
    } catch {
      toast.error(t('treatmentsAdmin.updateFailed'));
    }
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={requestClose}
      onExitComplete={onClose}
      title={localizedName(treatmentType, i18n.language)}
      maxWidth="max-w-lg"
    >
      <div className="space-y-2">
        {/* Status */}
        <div className="rounded-xl p-3" style={cardStyle}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 opacity-60" />
              <span className="text-sm font-bold">{t('treatmentsAdmin.status')}</span>
              <span className="text-sm font-semibold opacity-70">
                {treatmentType.isActive ? t('treatmentsAdmin.active') : t('treatmentsAdmin.inactive')}
              </span>
            </div>
            <button
              onClick={handleToggleActive}
              disabled={updateMutation.isPending}
              className="flex items-center gap-1.5 px-2.5 py-1 glass-surface rounded-lg text-xs font-bold hover:bg-white/10 transition-colors disabled:opacity-50 cursor-pointer"
              style={actionBtnStyle}
            >
              {updateMutation.isPending ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : treatmentType.isActive ? (
                <>
                  <ToggleRight className="w-3.5 h-3.5" />
                  {t('treatmentsAdmin.inactive')}
                </>
              ) : (
                <>
                  <ToggleLeft className="w-3.5 h-3.5" />
                  {t('treatmentsAdmin.active')}
                </>
              )}
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="rounded-xl p-2 text-center" style={cardStyle}>
          <FileText className="w-5 h-5 mx-auto mb-1 opacity-60" />
          <p className="text-xl font-bold">{treatmentType.reportCount ?? 0}</p>
          <p className="text-xs font-semibold opacity-60">{t('treatmentsAdmin.reports')}</p>
        </div>

        {/* Code (read-only) */}
        <div className="rounded-xl p-3" style={cardStyle}>
          <div className="flex items-center gap-2 text-sm">
            <Hash className="w-3.5 h-3.5 opacity-50" />
            <span className="font-bold">{t('treatmentsAdmin.code')}:</span>
            <span className="opacity-70 font-mono">{treatmentType.code}</span>
          </div>
        </div>

        {/* Editable fields */}
        <div className="rounded-xl p-3 space-y-2.5" style={cardStyle}>
          {/* Name */}
          <div className="space-y-1">
            <label className="flex items-center gap-1.5 text-xs font-bold opacity-70">
              <FileText className="w-3.5 h-3.5" />
              {t('treatmentsAdmin.name')}
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 rounded-lg text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-400"
              style={inputStyle}
            />
          </div>

          {/* Name (English) */}
          <div className="space-y-1">
            <label className="flex items-center gap-1.5 text-xs font-bold opacity-70">
              <FileText className="w-3.5 h-3.5" />
              {t('treatmentsAdmin.nameEn')}
            </label>
            <input
              type="text"
              value={nameEn}
              onChange={(e) => setNameEn(e.target.value)}
              className="w-full px-3 py-2 rounded-lg text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-400"
              style={inputStyle}
            />
          </div>

          {/* Description */}
          <div className="space-y-1">
            <label className="flex items-center gap-1.5 text-xs font-bold opacity-70">
              <FileText className="w-3.5 h-3.5" />
              {t('treatmentsAdmin.description')}
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 rounded-lg text-sm font-semibold resize-none focus:outline-none focus:ring-2 focus:ring-blue-400"
              style={inputStyle}
            />
          </div>

          {/* Color */}
          <div className="space-y-1">
            <label className="flex items-center gap-1.5 text-xs font-bold opacity-70">
              <Palette className="w-3.5 h-3.5" />
              {t('treatmentsAdmin.color')}
            </label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={color || '#1565c0'}
                onChange={(e) => setColor(e.target.value)}
                className="w-10 h-10 rounded-lg cursor-pointer border-0 p-0"
              />
              <input
                type="text"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                placeholder="#1565c0"
                className="flex-1 px-3 py-2 rounded-lg text-sm font-semibold font-mono focus:outline-none focus:ring-2 focus:ring-blue-400"
                style={inputStyle}
              />
            </div>
          </div>
        </div>

        {/* Save button */}
        {hasChanges && (
          <button
            onClick={handleSave}
            disabled={updateMutation.isPending || !name.trim()}
            className="flex items-center justify-center gap-2 w-full px-3 py-2.5 rounded-xl font-bold transition-transform cursor-pointer disabled:opacity-50"
            style={{
              backgroundColor: MODAL_COLORS.header,
              border: `1px solid ${MODAL_COLORS.border}`,
              color: '#fff',
            }}
          >
            {updateMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {t('common.save')}
          </button>
        )}

        {/* Compatible Helmets */}
        <div className="rounded-xl p-3" style={cardStyle}>
          <div className="flex items-center gap-2 mb-2">
            <HardHat className="w-4 h-4 opacity-60" />
            <span className="text-sm font-bold">{t('treatmentsAdmin.compatibleHelmets')}</span>
          </div>
          {allHelmetTypes.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {allHelmetTypes.map((ht) => {
                const isActive = treatmentType.compatibleHelmetCodes?.includes(ht.code) ?? false;
                return (
                  <button
                    key={ht.id}
                    type="button"
                    onClick={async () => {
                      triggerHapticFeedback();
                      const currentCodes = treatmentType.compatibleHelmetCodes ?? [];
                      const currentIds = allHelmetTypes
                        .filter(h => currentCodes.includes(h.code))
                        .map(h => h.id);
                      const newIds = isActive
                        ? currentIds.filter(id => id !== ht.id)
                        : [...currentIds, ht.id];
                      try {
                        await helmetMutation.mutateAsync({
                          treatmentTypeId: treatmentType.id,
                          helmetTypeIds: newIds,
                        });
                        toast.success(t('treatmentsAdmin.helmetConnectionUpdated'));
                      } catch {
                        toast.error(t('treatmentsAdmin.helmetConnectionFailed'));
                      }
                    }}
                    disabled={helmetMutation.isPending}
                    className="px-2 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer disabled:opacity-50"
                    style={{
                      backgroundColor: isActive ? MODAL_COLORS.header : MODAL_COLORS.surface,
                      color: isActive ? '#fff' : MODAL_COLORS.text,
                      border: `1px solid ${MODAL_COLORS.border}`,
                    }}
                  >
                    {ht.name}
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="text-xs opacity-50">{t('treatmentsAdmin.noCompatibleHelmets')}</p>
          )}
        </div>

        {/* Dates */}
        <div className="rounded-xl p-3 space-y-1" style={cardStyle}>
          <div className="flex items-center gap-2 text-xs">
            <Calendar className="w-3.5 h-3.5 opacity-50" />
            <span className="font-bold">{t('treatmentsAdmin.createdAt')}:</span>
            <span className="opacity-70">{new Date(treatmentType.createdAt).toLocaleDateString()}</span>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <Clock className="w-3.5 h-3.5 opacity-50" />
            <span className="font-bold">{t('treatmentsAdmin.updatedAt')}:</span>
            <span className="opacity-70">{new Date(treatmentType.updatedAt).toLocaleDateString()}</span>
          </div>
        </div>

        {/* Delete */}
        <button
          onClick={() => {
            triggerHapticFeedback();
            openConfirm({
              title: t('treatmentsAdmin.delete'),
              message: t('treatmentsAdmin.deleteConfirm'),
              variant: 'danger',
              confirmLabel: t('common.delete'),
              onConfirm: async () => {
                try {
                  await deleteMutation.mutateAsync(treatmentType.id);
                  toast.success(t('treatmentsAdmin.deleteSuccess'));
                  requestClose();
                } catch {
                  toast.error(t('treatmentsAdmin.deleteFailed'));
                }
              },
            });
          }}
          disabled={deleteMutation.isPending}
          className="flex items-center justify-center gap-2 w-full px-3 py-2.5 rounded-xl font-bold hover:translate-x-[1px] hover:translate-y-[1px] transition-transform cursor-pointer disabled:opacity-50"
          style={{
            backgroundColor: 'rgba(254, 226, 226, 0.6)',
            border: `1px solid ${MODAL_COLORS.border}`,
            color: '#B91C1C',
          }}
        >
          <Trash2 className="w-4 h-4" />
          {t('treatmentsAdmin.delete')}
        </button>
      </div>
    </FormModal>
  );
}
